import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Pencil, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const empty = {
  name: '', months: 12, plan_type: 'auto',
  business_charge_percent: 0, registration_fee: 0, processing_fee: 0,
  document_charges: 0, insurance_fee: 0, tax_percent: 0, other_charges: 0,
  min_down_payment_percent: 0, late_payment_charge: 0,
  is_active: true,
};

export default function InstallmentPlanMaster() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const canManage = ['Super Admin', 'Admin'].includes(user?.role);
  const canDelete = user?.role === 'Super Admin';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/installment-plans`, { withCredentials: true });
      setPlans(data);
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setForm(empty); setEditingId(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p });
    setEditingId(p.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        plan_type: 'auto',
        months: Number(form.months),
        business_charge_percent: Number(form.business_charge_percent),
        registration_fee: Number(form.registration_fee),
        processing_fee: Number(form.processing_fee),
        document_charges: Number(form.document_charges),
        insurance_fee: Number(form.insurance_fee),
        tax_percent: Number(form.tax_percent),
        other_charges: Number(form.other_charges),
        min_down_payment_percent: Number(form.min_down_payment_percent),
        late_payment_charge: Number(form.late_payment_charge || 0),
        is_active: !!form.is_active,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/installment-plans/${editingId}`, payload, { withCredentials: true });
        toast.success('Plan updated');
      } else {
        await axios.post(`${API_URL}/api/installment-plans`, payload, { withCredentials: true });
        toast.success('Plan created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this installment plan?')) return;
    try {
      await axios.delete(`${API_URL}/api/installment-plans/${id}`, { withCredentials: true });
      toast.success('Plan deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSeed = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/installment-plans/seed-defaults`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="installment-plan-master">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="plans-title">Installment Plan Master</h1>
          <p className="text-slate-400">Define plans with business charge %, fees and down payment rules</p>
        </div>
        <div className="flex space-x-2">
          {canManage && plans.length === 0 && (
            <Button onClick={handleSeed} variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white" data-testid="seed-plans-button">
              <Sparkles className="h-4 w-4 mr-2" /> Seed Defaults
            </Button>
          )}
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-plan-button">
                  <Plus className="h-4 w-4 mr-2" /> Add Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl">
                <DialogHeader>
                  <DialogTitle className="text-white">{editingId ? 'Edit' : 'Create'} Installment Plan</DialogTitle>
                  <DialogDescription className="text-slate-400">Dynamic terms — every plan will drive its own agreement math.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-slate-300">Plan Name *</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="plan-name-input" className="bg-slate-800 border-slate-700 text-white" />
                  </div>

                  {[
                    ['months', 'Months *'],
                    ['business_charge_percent', 'Business Charge %'],
                    ['tax_percent', 'Tax %'],
                    ['min_down_payment_percent', 'Min Down Payment %'],
                    ['registration_fee', 'Registration Fee (PKR)'],
                    ['processing_fee', 'Processing Fee (PKR)'],
                    ['document_charges', 'Document Charges (PKR)'],
                    ['insurance_fee', 'Insurance Fee (PKR)'],
                    ['other_charges', 'Other Charges (PKR)'],
                    ['late_payment_charge', 'Late Payment Charge (PKR — applied only on overdue installments, NOT at agreement creation)'],
                  ].map(([k, l]) => (
                    <div key={k} className="space-y-2">
                      <Label className="text-slate-300">{l}</Label>
                      <Input type="number" value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        data-testid={`plan-${k}-input`}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                  ))}
                  <div className="flex items-center justify-between col-span-2 border-t border-slate-800 pt-3">
                    <Label className="text-slate-300">Active</Label>
                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="plan-active-switch" />
                  </div>
                  <div className="col-span-2 flex justify-end space-x-2">
                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                    <Button type="submit" data-testid="submit-plan-button"
                      className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50">
                      {editingId ? 'Update' : 'Create'} Plan
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center space-x-2 text-slate-300">
            <Package className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-white">Configured Plans</h2>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div>
            : plans.length === 0 ? (
              <div className="text-center py-8 text-slate-400" data-testid="no-plans-message">
                No plans configured yet. {canManage && 'Click "Seed Defaults" to add the 9 standard plans.'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Months</TableHead>
                    <TableHead className="text-slate-400">Business %</TableHead>
                    <TableHead className="text-slate-400">Reg Fee</TableHead>
                    <TableHead className="text-slate-400">Proc Fee</TableHead>
                    <TableHead className="text-slate-400">Min DP %</TableHead>
                    <TableHead className="text-slate-400">Late Fee</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    {canManage && <TableHead className="text-slate-400">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="plan-row">
                      <TableCell className="text-white font-medium">{p.name}</TableCell>
                      <TableCell className="text-slate-300">{p.months}</TableCell>
                      <TableCell className="text-amber-500">{p.business_charge_percent}%</TableCell>
                      <TableCell className="text-slate-300">PKR {p.registration_fee?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300">PKR {p.processing_fee?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300">{p.min_down_payment_percent}%</TableCell>
                      <TableCell className="text-slate-300">PKR {p.late_payment_charge?.toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs ${p.is_active ? 'bg-emerald-500/20 text-emerald-500' : 'bg-slate-700/50 text-slate-400'}`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <div className="flex items-center space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)}
                              data-testid={`edit-plan-${p.id}`}
                              className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-8 w-8 p-0" title="Edit">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canDelete && (
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)}
                                data-testid={`delete-plan-${p.id}`}
                                className="text-red-400 hover:text-red-300 hover:bg-slate-800 h-8 w-8 p-0" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
